/**
 * Amount — precision-safe token amount using bignumber.js.
 *
 * Ported verbatim from the CLI tool. Works in Workers (bignumber.js has
 * zero node deps) and in the browser. Used by worker services and the
 * frontend fee estimator.
 */

import BigNumber from "bignumber.js";

import type { Amount, WireAmount } from "./types.js";

BigNumber.config({
  DECIMAL_PLACES: 40,
  ROUNDING_MODE: BigNumber.ROUND_DOWN,
  EXPONENTIAL_AT: [-20, 40],
});

export function makeAmount(
  raw: BigNumber.Value,
  decimals: number,
  symbol: string
): Amount {
  const big = new BigNumber(raw);
  if (!big.isFinite() || big.isNegative()) {
    throw new Error(`Invalid amount raw=${raw} for ${symbol}`);
  }
  return { raw: big.integerValue(BigNumber.ROUND_DOWN), decimals, symbol };
}

export function fromNatural(
  natural: BigNumber.Value,
  decimals: number,
  symbol: string
): Amount {
  const big = new BigNumber(natural);
  if (!big.isFinite() || big.isNegative()) {
    throw new Error(`Invalid natural amount ${natural} for ${symbol}`);
  }
  const raw = big.shiftedBy(decimals).integerValue(BigNumber.ROUND_DOWN);
  return { raw, decimals, symbol };
}

export function toNatural(amount: Amount): BigNumber {
  return amount.raw.shiftedBy(-amount.decimals);
}

export function formatAmount(amount: Amount, displayDecimals?: number): string {
  const natural = toNatural(amount);
  const dp = displayDecimals ?? Math.min(amount.decimals, 6);
  return `${natural.toFixed(dp)} ${amount.symbol}`;
}

export function scale(amount: Amount, multiplier: BigNumber.Value): Amount {
  const m = new BigNumber(multiplier);
  return {
    raw: amount.raw.multipliedBy(m).integerValue(BigNumber.ROUND_DOWN),
    decimals: amount.decimals,
    symbol: amount.symbol,
  };
}

export function applySlippage(amount: Amount, slippage: number): Amount {
  if (slippage < 0 || slippage > 1) {
    throw new Error(`slippage must be in [0, 1], got ${slippage}`);
  }
  return scale(amount, 1 - slippage);
}

export function addAmounts(a: Amount, b: Amount): Amount {
  if (a.decimals !== b.decimals || a.symbol !== b.symbol) {
    throw new Error(
      `Cannot add ${a.symbol}(${a.decimals}) and ${b.symbol}(${b.decimals})`
    );
  }
  return { raw: a.raw.plus(b.raw), decimals: a.decimals, symbol: a.symbol };
}

export function subAmounts(a: Amount, b: Amount): Amount {
  if (a.decimals !== b.decimals || a.symbol !== b.symbol) {
    throw new Error(
      `Cannot subtract ${a.symbol}(${a.decimals}) and ${b.symbol}(${b.decimals})`
    );
  }
  return { raw: a.raw.minus(b.raw), decimals: a.decimals, symbol: a.symbol };
}

export function ltAmount(a: Amount, b: Amount): boolean {
  if (a.decimals !== b.decimals) {
    throw new Error(
      `Cannot compare ${a.symbol}(${a.decimals}) with ${b.symbol}(${b.decimals})`
    );
  }
  return a.raw.lt(b.raw);
}

/**
 * Cross-boundary serialisation. BigNumber isn't structured-clone safe, so
 * the worker stringifies before returning and the frontend rehydrates.
 */
export function toWire(amount: Amount): WireAmount {
  return {
    raw_str: amount.raw.toFixed(0),
    decimals: amount.decimals,
    symbol: amount.symbol,
  };
}

export function fromWire(w: WireAmount): Amount {
  return makeAmount(w.raw_str, w.decimals, w.symbol);
}
